import { Loader2 } from 'lucide-react'
import { Button, Input, Label, Separator } from '@teach/ui'
import { useLanguage } from '@/hooks/useLanguage'
import { authT } from '../../locale'
import { GoogleIcon } from './GoogleIcon'
import { AuthTerms } from './AuthTerms'

interface AuthEmailFormProps {
  email: string
  setEmail: (value: string) => void
  emailError: string | null
  accountNotFound: boolean
  isLoading: boolean
  isGoogleLoading: boolean
  onSubmit: (e: React.FormEvent) => void
  onGoogle: () => void
}

export const AuthEmailForm = ({
  email,
  setEmail,
  emailError,
  accountNotFound,
  isLoading,
  isGoogleLoading,
  onSubmit,
  onGoogle,
}: AuthEmailFormProps) => {
  const { language } = useLanguage()
  const l = authT[language]
  // white by design: this form renders on the fixed-dark Auth hero, not app surface
  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={onGoogle}
        disabled={isGoogleLoading || isLoading}
        className="w-full h-11 bg-transparent border-border hover:bg-white/5 text-white hover:text-white font-semibold"
      >
        {isGoogleLoading ? (
          <Loader2 className="w-4 h-4 mr-3 animate-spin" />
        ) : (
          <span className="mr-3">
            <GoogleIcon />
          </span>
        )}
        {l.google}
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full bg-white/10" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-background px-4 text-white/50">{l.or}</span>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white font-semibold">
            {l.emailLabel}
          </Label>
          <Input
            id="email"
            type="email"
            placeholder={l.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`h-11 border-border text-white placeholder:text-white/40 focus-visible:border-white/30 ${
              emailError ? 'border-destructive' : ''
            }`}
          />
          {accountNotFound ? (
            <p className="text-sm text-sky-300 leading-relaxed">
              {l.errUserNotFoundBefore}
              <a
                href="https://learn.open-academy.app"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-sky-200 transition-colors"
              >
                learn.open-academy.app
              </a>
              {l.errUserNotFoundAfter}
            </p>
          ) : emailError ? (
            <p className="text-sm text-destructive">{emailError}</p>
          ) : null}
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 bg-white hover:bg-white/90 text-background font-medium mt-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {l.submitting}
            </>
          ) : (
            l.submit
          )}
        </Button>
      </form>

      <AuthTerms />
    </>
  )
}
